import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import { money, formatDate } from "@/lib/api";
import { makeTranslator } from "@/lib/onboarding_i18n";
import { Printer, ShieldCheck } from "lucide-react";
import {
  labels as disclosureLabels,
  deriveDisclosureTotalsFromPublic,
} from "@/lib/disclosure";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phase 2 Step 4: customer-facing disclosure wording (badge label, legend,
// T&M section title, etc.) is centralized in `/lib/disclosure.js`. Locally
// we only render the visual chrome.
const AllowanceBadge = ({ lang = "en" }) => (
  <span
    data-testid="allowance-badge"
    className="ml-2 inline-block align-middle px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-wider uppercase bg-[var(--jc-bg)] border border-[var(--jc-border-strong)] text-[var(--jc-text-soft)]"
  >
    {disclosureLabels(lang).allowance_badge}
  </span>
);

// Slice 2D4 Batch 3 · PublicDocs is a customer-facing page. Every label
// is looked up against the saved doc language (data.language) via a
// makeTranslator-bound `dt`. The public shell / loading / error boxes
// render before the doc is fetched, so they default to English chrome.
const PublicShell = ({ children, kind, lang = "en", company = null, token = null }) => {
  const dt = makeTranslator(lang);
  const name = (company?.name || "").trim();
  const hasLogo = !!(company?.has_logo && token);
  const logoUrl = hasLogo ? `${API}/public/document-logo/${token}` : null;
  const licenseLabel = lang === "es" ? "Licencia" : "License";
  const meta = [];
  if (company?.license_number) {
    meta.push(company.license_state
      ? `${licenseLabel} ${company.license_number} · ${company.license_state}`
      : `${licenseLabel} ${company.license_number}`);
  }
  if (company?.phone) meta.push(company.phone);
  if (company?.website) meta.push(company.website.replace(/^https?:\/\//, ""));
  return (
    <div className="min-h-screen bg-[var(--jc-bg)]" data-testid={`public-${kind}-page`}>
      {/* Contractor-first header — primary customer-facing identity. When
          the contractor has uploaded a logo we render the frozen bytes
          served by the shared token-gated /api/public/document-logo/{token}
          endpoint. Otherwise we fall back cleanly to the company name only.
          The JobClose AI mark is intentionally NOT rendered here; JobClose
          AI attribution is reserved for the subtle footer treatment below. */}
      <header className="bg-white border-b border-[var(--jc-border)] print:border-b print:border-[var(--jc-border)]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0 flex items-center gap-3" data-testid="public-doc-brand">
            {hasLogo ? (
              <img
                src={logoUrl}
                alt={name ? `${name} logo` : "Contractor logo"}
                className="h-14 w-auto max-w-[220px] object-contain select-none"
                draggable={false}
                data-testid="public-doc-logo"
              />
            ) : null}
            <div className="min-w-0">
              <div
                className="font-display text-[20px] sm:text-[22px] font-extrabold tracking-tight text-[var(--jc-text)] leading-tight truncate"
                data-testid="public-doc-company-name"
              >
                {name || " "}
              </div>
              {meta.length ? (
                <div
                  className="mt-0.5 text-[12.5px] text-[var(--jc-text-soft)] truncate"
                  data-testid="public-doc-company-meta"
                >
                  {meta.join(" · ")}
                </div>
              ) : null}
            </div>
          </div>
          <div className="text-xs text-[var(--jc-text-soft)] hidden sm:flex items-center gap-1.5 print:hidden shrink-0">
            <ShieldCheck size={14} className="text-[var(--jc-teal)]" /> {dt("public_docs.secure_link")}
          </div>
        </div>
      </header>
      <main className="py-6 lg:py-10">{children}</main>
      {/* Subtle JobClose AI attribution — footer only. Hidden on print
          so the customer's saved PDF is a pure contractor-branded artefact
          per PO direction. */}
      <footer className="border-t border-[var(--jc-border)] py-6 mt-10 print:hidden">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11.5px] text-[var(--jc-text-soft)]">
          <div>{dt("public_docs.powered_by_line")}</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-[var(--jc-text)]">{dt("public_docs.privacy_link")}</Link>
            <Link to="/terms" className="hover:text-[var(--jc-text)]">{dt("public_docs.terms_link")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Loading = () => {
  const dt = makeTranslator("en");
  return (
    <PublicShell kind="loading">
      <div className="max-w-4xl mx-auto px-6 py-20 text-center text-[var(--jc-text-soft)]">{dt("public_docs.loading")}</div>
    </PublicShell>
  );
};

const ErrorBox = ({ error }) => {
  const dt = makeTranslator("en");
  return (
    <PublicShell kind="error">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="jc-card p-10 text-center">
          <div className="font-display text-2xl">{dt("public_docs.link_gone_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-3">
            {error?.response?.status === 410
              ? dt("public_docs.link_expired_body")
              : dt("public_docs.link_missing_body")}
          </p>
        </div>
      </div>
    </PublicShell>
  );
};

const localizeProposalStatus = (dt, status) => {
  if (!status) return status;
  const key = `public_docs.proposal_status_labels.${status}`;
  const val = dt(key);
  return val === key ? status : val;
};

export const PublicProposal = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/proposals/${token}`).then((r) => setData(r.data)).catch(setErr);
  }, [token]);

  if (err) return <ErrorBox error={err} />;
  if (!data) return <Loading />;

  const lang = data.language || "en";
  const dt = makeTranslator(lang);
  const L = disclosureLabels(lang);
  const { tmEstimate } = deriveDisclosureTotalsFromPublic(data);

  return (
    <PublicShell kind="proposal" lang={lang} company={data.company} token={token}>
      <div className="max-w-4xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-end mb-4">
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white hover:bg-[var(--jc-bg)] print:hidden" data-testid="public-print-btn">
            <Printer size={14} /> {dt("public_docs.print_save_pdf")}
          </button>
        </div>
        <article className="bg-white border border-[var(--jc-border)] rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 lg:p-10 print:shadow-none print:border-0">
          <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between border-b border-[var(--jc-border)] pb-6 gap-4">
            <div>
              <div className="overline">{dt("public_docs.proposal_kind_label")}</div>
              <h1 className="font-display text-3xl mt-1">{data.customer?.name}</h1>
              <div className="text-sm text-[var(--jc-text-soft)] mt-1">{data.customer?.address}</div>
            </div>
            <div className="text-sm text-[var(--jc-text-soft)] sm:text-right">
              <div>{dt("public_docs.contract_issued_prefix")}: {formatDate(data.created_at, lang)}</div>
              <div className="capitalize">{dt("public_docs.proposal_status_label")}: <span className="font-semibold text-[var(--jc-text)]">{localizeProposalStatus(dt, data.status)}</span></div>
            </div>
          </header>

          <section className="mt-6 text-sm">
            <div className="overline mb-2">{dt("public_docs.proposal_scope_of_work")}</div>
            <p className="leading-relaxed">{data.scope}</p>
            {data.included_items?.length > 0 && (<><div className="overline mt-5 mb-2">{dt("public_docs.proposal_included")}</div><ul className="list-disc pl-6 space-y-1">{data.included_items.map((x, i) => <li key={i}>{x}</li>)}</ul></>)}
            {data.excluded_items?.length > 0 && (<><div className="overline mt-5 mb-2">{dt("public_docs.proposal_excluded")}</div><ul className="list-disc pl-6 space-y-1">{data.excluded_items.map((x, i) => <li key={i}>{x}</li>)}</ul></>)}
          </section>

          <section className="mt-6 overflow-x-auto">
            <div className="overline mb-2">{dt("public_docs.proposal_pricing")}</div>
            <table className="w-full text-sm border border-[var(--jc-border)]">
              <thead className="bg-[var(--jc-bg)]"><tr><th className="text-left px-3 py-2">{dt("public_docs.proposal_item")}</th><th className="text-right px-3 py-2">{dt("public_docs.proposal_qty")}</th><th className="text-right px-3 py-2">{dt("public_docs.proposal_total")}</th></tr></thead>
              <tbody className="divide-y divide-[var(--jc-border)]">
                {(data.line_items || []).map((l, i) => (
                  <tr key={i}><td className="px-3 py-2"><div className="font-semibold">{l.name || "—"}{l.is_allowance && <AllowanceBadge lang={lang} />}</div><div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div></td><td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td><td className="px-3 py-2 text-right font-mono-jc">{money(l.total)}</td></tr>
                ))}
              </tbody>
              <tfoot className="bg-[var(--jc-bg)] text-sm">
                <tr><td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{dt("public_docs.proposal_subtotal")}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(data.totals?.subtotal)}</td></tr>
                <tr><td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{dt("public_docs.proposal_tax")}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(data.totals?.tax)}</td></tr>
                <tr><td colSpan={2} className="px-3 py-2 text-right font-semibold">{dt("public_docs.proposal_total")}</td><td className="px-3 py-2 text-right font-mono-jc font-bold">{money(data.totals?.total)}</td></tr>
                <tr><td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{dt("public_docs.proposal_deposit")}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(data.totals?.deposit)}</td></tr>
                {tmEstimate > 0 && (
                  <tr data-testid="tm-reserve-summary"><td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)] italic">{L.tm_reserve_label} <span className="text-[10px] uppercase tracking-wider">· {L.tm_reserve_note}</span></td><td className="px-3 py-1.5 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(tmEstimate)}</td></tr>
                )}
              </tfoot>
            </table>
            {(data.line_items || []).some((l) => l.is_allowance) && (
              <p data-testid="allowance-legend" className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">
                <span className="font-semibold">{L.allowance_badge}:</span> {L.allowance_legend}
              </p>
            )}
          </section>

          {data.tm_lines?.length > 0 && (
            <section className="mt-6 overflow-x-auto" data-testid="tm-section">
              <div className="overline mb-2">{L.tm_section_title}</div>
              <table className="w-full text-sm border border-[var(--jc-border)]">
                <thead className="bg-[var(--jc-bg)]"><tr><th className="text-left px-3 py-2">{L.item}</th><th className="text-right px-3 py-2">{L.qty}</th><th className="text-right px-3 py-2">{L.est_total}</th></tr></thead>
                <tbody className="divide-y divide-[var(--jc-border)]">
                  {data.tm_lines.map((l, i) => (
                    <tr key={i}><td className="px-3 py-2"><div className="font-semibold">{l.name || "—"}</div><div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div></td><td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td><td className="px-3 py-2 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(l.total)}</td></tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">{L.tm_legend}</p>
            </section>
          )}

          <section className="grid sm:grid-cols-2 gap-6 mt-6 text-sm">
            <div><div className="overline mb-1">{dt("public_docs.proposal_payment_schedule")}</div><p>{data.payment_schedule || "—"}</p></div>
            <div><div className="overline mb-1">{dt("public_docs.proposal_timeline")}</div><p>{data.timeline || "—"}</p></div>
            <div className="sm:col-span-2"><div className="overline mb-1">{dt("public_docs.proposal_next_steps")}</div><p>{data.next_steps || dt("public_docs.proposal_next_steps_fallback")}</p></div>
          </section>

          <footer className="mt-8 pt-6 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]">
            {data.disclaimer}
            <div className="mt-2">{dt("public_docs.link_expires_prefix", formatDate(data.expires_at, lang))}</div>
          </footer>
        </article>
      </div>
    </PublicShell>
  );
};

export const PublicContract = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [signing, setSigning] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/contracts/${token}`).then((r) => setData(r.data)).catch(setErr);
  }, [token]);

  const lang = data?.language || "en";
  const dt = makeTranslator(lang);

  const sign = async () => {
    if (signing.trim().length < 2) { setError(dt("public_docs.contract_err_missing_name")); return; }
    setError(""); setSubmitting(true);
    try {
      const { data: signed } = await axios.post(`${API}/public/contracts/${token}/sign`, { signature_name: signing });
      setData(signed);
    } catch (e) {
      setError(e?.response?.data?.detail || dt("public_docs.contract_err_sign_failed"));
    } finally { setSubmitting(false); }
  };

  if (err) return <ErrorBox error={err} />;
  if (!data) return <Loading />;

  const L = disclosureLabels(lang);
  const { allowanceTotal, tmEstimate } = deriveDisclosureTotalsFromPublic(data);

  return (
    <PublicShell kind="contract" lang={lang} company={data.company} token={token}>
      <div className="max-w-4xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-end mb-4">
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white hover:bg-[var(--jc-bg)] print:hidden" data-testid="public-print-btn">
            <Printer size={14} /> {dt("public_docs.print_save_pdf")}
          </button>
        </div>
        <article className="bg-white border border-[var(--jc-border)] rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 lg:p-10 print:shadow-none print:border-0">
          <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between border-b border-[var(--jc-border)] pb-6 gap-4">
            <div>
              <div className="overline">{dt("public_docs.contract_kind_label")}</div>
              <h1 className="font-display text-3xl mt-1">{dt("public_docs.contract_agreement_with", data.customer?.name || "")}</h1>
              <div className="text-sm text-[var(--jc-text-soft)] mt-1">{data.customer?.address}</div>
            </div>
            <div className="text-sm text-[var(--jc-text-soft)] sm:text-right">
              <div>{dt("public_docs.contract_issued_prefix")}: {formatDate(data.created_at, lang)}</div>
              {/* Customer-facing wording — never expose internal workflow status (e.g. "Draft") to homeowners */}
              <div className="font-semibold text-[var(--jc-text)]">{data.status === "signed" ? dt("public_docs.contract_signed") : dt("public_docs.contract_awaiting_signature")}</div>
            </div>
          </header>

          <section className="mt-6 text-sm space-y-5">
            <Block title={dt("public_docs.contract_section_scope")} body={data.scope} />
            <Block title={dt("public_docs.contract_section_terms")} body={data.contract_terms} />
            <Block title={dt("public_docs.contract_section_change_orders")} body={data.change_order_language} />
            <Block title={dt("public_docs.contract_section_customer_responsibilities")} body={data.customer_responsibilities} />
            <Block title={dt("public_docs.contract_section_exclusions")} body={data.exclusions} />
            <div>
              <div className="overline mb-1">{dt("public_docs.contract_price")}</div>
              <div className="font-mono-jc text-2xl font-bold">{money(data.totals?.total)}</div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-1">{dt("public_docs.contract_deposit_due_prefix")}: {money(data.totals?.deposit)} · {dt("public_docs.contract_balance_prefix")}: {money(data.totals?.balance_due)}</div>
              {allowanceTotal > 0 && (
                <div data-testid="contract-allowance-summary" className="text-xs text-[var(--jc-text-soft)] mt-2">
                  <span className="font-semibold">{L.allowance_total_label}:</span> {money(allowanceTotal)}
                </div>
              )}
              {tmEstimate > 0 && (
                <div data-testid="contract-tm-summary" className="text-xs text-[var(--jc-text-soft)] mt-1 italic">
                  {L.tm_reserve_label}: {money(tmEstimate)} — {L.tm_reserve_note}
                </div>
              )}
            </div>
          </section>

          {data.tm_lines?.length > 0 && (
            <section className="mt-6 overflow-x-auto" data-testid="contract-tm-section">
              <div className="overline mb-2">{L.tm_section_title}</div>
              <table className="w-full text-sm border border-[var(--jc-border)]">
                <thead className="bg-[var(--jc-bg)]"><tr><th className="text-left px-3 py-2">{L.item}</th><th className="text-right px-3 py-2">{L.qty}</th><th className="text-right px-3 py-2">{L.est_total}</th></tr></thead>
                <tbody className="divide-y divide-[var(--jc-border)]">
                  {data.tm_lines.map((l, i) => (
                    <tr key={i}><td className="px-3 py-2"><div className="font-semibold">{l.name || "—"}</div><div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div></td><td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td><td className="px-3 py-2 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(l.total)}</td></tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">{L.tm_legend}</p>
            </section>
          )}

          <section className="mt-8">
            <div className="overline mb-2">{dt("public_docs.contract_signature_heading")}</div>
            {data.status === "signed" ? (
              <div className="border border-[var(--jc-border)] rounded-md p-5 bg-[var(--jc-bg)]">
                <div className="font-display text-3xl italic text-[var(--jc-navy)]">{data.signature_name}</div>
                <div className="text-xs text-[var(--jc-text-soft)] mt-2">{dt("public_docs.contract_signed_on", formatDate(data.signed_at, lang))}</div>
              </div>
            ) : (
              <div className="border border-[var(--jc-border)] rounded-md p-5">
                <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">{dt("public_docs.contract_sign_input_label")}</label>
                <input
                  value={signing} onChange={(e) => setSigning(e.target.value)}
                  data-testid="public-sign-input"
                  placeholder={dt("public_docs.contract_sign_input_placeholder")}
                  className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
                />
                {error && <div className="text-sm text-[var(--jc-red)] mt-2">{error}</div>}
                <button
                  onClick={sign} disabled={submitting}
                  data-testid="public-sign-btn"
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold jc-btn-primary disabled:opacity-60"
                >
                  {submitting ? dt("public_docs.contract_sign_submitting") : dt("public_docs.contract_sign_btn")}
                </button>
              </div>
            )}
          </section>

          <footer className="mt-8 pt-6 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)] leading-relaxed">
            <div className="overline mb-1">{dt("public_docs.contract_legal_disclaimer_heading")}</div>
            <p className="whitespace-pre-line">{data.legal_disclaimer}</p>
            <div className="mt-2">{dt("public_docs.link_expires_prefix", formatDate(data.expires_at, lang))}</div>
          </footer>
        </article>
      </div>
    </PublicShell>
  );
};

// Slice 2P1 Pass C · Customer-facing Project Closeout view.
// Read-only. No login. No upload. No chat. No signature. No payment.
// Renders only the whitelisted safe payload returned by
// GET /api/public/closeout-packets/{token}. Levi welcomes and frames
// the view inline with a deterministic message — no API, no LLM.
export const PublicCloseoutPacket = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/closeout-packets/${token}`)
      .then((r) => setData(r.data)).catch(setErr);
  }, [token]);
  if (err) return <ErrorBox error={err} />;
  if (!data) return <Loading />;
  const lang = data.language || "en";
  const dt = makeTranslator(lang);
  const company = data.company?.name || "";
  const scope = Array.isArray(data.scope_items_safe) ? data.scope_items_safe : [];
  return (
    <PublicShell kind="closeout" lang={lang} company={data.company} token={token}>
      <div className="max-w-4xl mx-auto px-4 lg:px-6">
        {/* Restrained Print / Save PDF control — reuses the approved
            Proposal / Contract / Change-Order pattern. print:hidden so
            the button never appears in the customer's saved PDF. */}
        <div className="flex items-center justify-end mb-4">
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white hover:bg-[var(--jc-bg)] print:hidden" data-testid="public-print-btn">
            <Printer size={14} /> {dt("public_docs.print_save_pdf")}
          </button>
        </div>
        <article
          data-testid="public-closeout-article"
          className="bg-white border border-[var(--jc-border)] rounded-sm p-6 lg:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)] print:shadow-none print:border-0"
        >
          <header className="mb-6">
            <div className="overline">{dt("public_closeout.overline")}</div>
            <h1
              data-testid="public-closeout-title"
              className="font-display text-2xl lg:text-3xl mt-1"
            >
              {dt("public_closeout.title")}
            </h1>
            {/* Frozen customer / project identity (Phase 1 shared standard).
                Prefer the enriched snapshot fields; fall back cleanly to the
                legacy flat customer_name value. Company name is intentionally
                NOT repeated here — the shared PublicShell already establishes
                the sender at the top of the page. */}
            <div className="text-sm text-[var(--jc-text-soft)] mt-1" data-testid="public-closeout-customer">
              {(data.customer?.name || data.customer_name) || ""}
              {data.project?.name ? <> · {data.project.name}</> : null}
            </div>
          </header>

          <section
            data-testid="public-closeout-job-meta"
            className="grid sm:grid-cols-2 gap-4 mb-6 text-sm"
          >
            <div>
              <div className="overline mb-1">{dt("public_closeout.jobsite")}</div>
              <div>{data.jobsite_address || "—"}</div>
            </div>
            <div>
              <div className="overline mb-1">{dt("public_closeout.project_type")}</div>
              <div>{data.project_type || "—"}</div>
            </div>
            {data.completion_date && (
              <div>
                <div className="overline mb-1">{dt("public_closeout.completion_date")}</div>
                <div>{formatDate(data.completion_date, lang)}</div>
              </div>
            )}
            {data.invoice_status && (
              <div>
                <div className="overline mb-1">{dt("public_closeout.invoice_status")}</div>
                <div>{data.invoice_status}</div>
              </div>
            )}
          </section>

          {data.work_completed_summary && (
            <section
              data-testid="public-closeout-work-summary"
              className="mb-6"
            >
              <div className="overline mb-1">{dt("public_closeout.work_summary")}</div>
              <p className="leading-relaxed whitespace-pre-wrap">
                {data.work_completed_summary}
              </p>
            </section>
          )}

          {data.completion_notes && (
            <Block
              title={dt("public_closeout.completion_notes")}
              body={data.completion_notes}
            />
          )}

          {scope.length > 0 && (
            <section
              data-testid="public-closeout-scope"
              className="mt-6"
            >
              <div className="overline mb-2">{dt("public_closeout.scope")}</div>
              <ul className="space-y-1.5 text-sm">
                {scope.map((it, i) => (
                  <li
                    key={i}
                    data-testid={`public-closeout-scope-item-${i}`}
                    className="flex items-baseline gap-3"
                  >
                    <span className="font-semibold flex-1">{it.name || "—"}</span>
                    {(it.quantity != null || it.unit) && (
                      <span className="text-[var(--jc-text-soft)] whitespace-nowrap">
                        {it.quantity ?? ""} {it.unit || ""}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <Block
              title={dt("public_closeout.workmanship_warranty")}
              body={data.workmanship_warranty_terms}
            />
            <Block
              title={dt("public_closeout.manufacturer_warranty")}
              body={data.manufacturer_warranty_notes}
            />
            <Block
              title={dt("public_closeout.maintenance_notes")}
              body={data.maintenance_notes}
            />
            <Block
              title={dt("public_closeout.warranty_contact")}
              body={data.warranty_contact_notes}
            />
            <Block
              title={dt("public_closeout.review_request")}
              body={data.review_request}
            />
            <Block
              title={dt("public_closeout.referral_request")}
              body={data.referral_request}
            />
            <Block
              title={dt("public_closeout.final_punch_list")}
              body={data.final_punch_list_notes}
            />
          </div>

          <footer className="mt-8 pt-6 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]">
            {dt("public_closeout.footer_note")}
          </footer>
        </article>
      </div>
    </PublicShell>
  );
};


// Phase 10 · PR-1 · Customer-facing Change Order review + sign surface.
// Reads change_order.language (Decision 3). Approval-as-signature
// (Decision 1) — no separate approve step. Secure-token possession +
// typed legal name (Decision 4). Bilingual EN/ES via onboarding_i18n.js.
export const PublicChangeOrder = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [signing, setSigning] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/change-orders/${token}`)
      .then((r) => setData(r.data)).catch(setErr);
  }, [token]);

  const lang = data?.language || "en";
  const dt = makeTranslator(lang);

  const sign = async () => {
    if (signing.trim().length < 2) {
      setError(dt("public_docs.co_err_missing_name"));
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const { data: signed } = await axios.post(
        `${API}/public/change-orders/${token}/sign`,
        { signature_name: signing },
      );
      setData(signed);
    } catch (e) {
      setError(e?.response?.data?.detail?.message
        || e?.response?.data?.detail?.error
        || dt("public_docs.co_err_sign_failed"));
    } finally { setSubmitting(false); }
  };

  if (err) return <ErrorBox error={err} />;
  if (!data) return <Loading />;

  const status = data.status || "";
  const isFullyExecuted = status === "fully_executed";
  const isPartiallySigned = status === "partially_signed";
  const customerSigner = (data.signers || []).find((s) => s.role === "customer");
  const already = !!customerSigner;
  const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);
  const fd = data.financial_delta || {};

  return (
    <PublicShell kind="change-order" lang={lang} company={data.company} token={token}>
      <div className="max-w-4xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => window.print()}
            data-testid="public-co-print-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white hover:bg-[var(--jc-bg)] print:hidden"
          >
            <Printer size={14} /> {dt("public_docs.print_save_pdf")}
          </button>
        </div>

        <article
          data-testid="public-co-article"
          className="bg-white border border-[var(--jc-border)] rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 lg:p-10 print:shadow-none print:border-0"
        >
          <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between border-b border-[var(--jc-border)] pb-6 gap-4">
            <div>
              <div className="overline">{dt("public_docs.co_kind_label")}</div>
              <h1 className="font-display text-3xl mt-1" data-testid="public-co-title">
                {dt("public_docs.co_number_prefix")} #{data.change_order_number}
              </h1>
              {data.customer?.name ? (
                <div className="text-sm text-[var(--jc-text-soft)] mt-1" data-testid="public-co-customer">
                  {data.customer.name}
                  {data.customer.address ? (
                    <>
                      <br />
                      {data.customer.address}
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="text-sm text-[var(--jc-text-soft)] sm:text-right">
              <div>{dt("public_docs.contract_issued_prefix")}: {formatDate(data.created_at, lang)}</div>
              <div className="font-semibold text-[var(--jc-text)]" data-testid="public-co-status">
                {isFullyExecuted
                  ? dt("public_docs.co_fully_executed")
                  : isPartiallySigned
                  ? dt("public_docs.co_partially_signed")
                  : dt("public_docs.co_awaiting_signature")}
              </div>
            </div>
          </header>

          <section className="mt-6 text-sm space-y-5">
            {data.scope_summary && (
              <Block title={dt("public_docs.co_section_scope_summary")} body={data.scope_summary} />
            )}
            <div>
              <div className="overline mb-1">{dt("public_docs.co_financial_delta")}</div>
              <div className="font-mono-jc text-2xl font-bold" data-testid="public-co-net-delta">
                {(fd.total_delta != null && fd.total_delta >= 0) ? "+" : ""}{money(fd.total_delta)}
              </div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-1">
                {dt("public_docs.co_revised_contract_prefix")}: {money(fd.revised_contract_value)}
              </div>
            </div>
            {data.schedule_delta && (data.schedule_delta.days_added || data.schedule_delta.new_target_completion_date) && (
              <div>
                <div className="overline mb-1">{dt("public_docs.co_schedule_delta")}</div>
                {data.schedule_delta.days_added != null && (
                  <div>{dt("public_docs.co_days_added_prefix")}: {data.schedule_delta.days_added}</div>
                )}
                {data.schedule_delta.new_target_completion_date && (
                  <div className="text-xs text-[var(--jc-text-soft)]">
                    {dt("public_docs.co_new_completion_prefix")}: {formatDate(data.schedule_delta.new_target_completion_date, lang)}
                  </div>
                )}
                {data.schedule_delta.notes && (
                  <p className="text-xs mt-1 whitespace-pre-line">{data.schedule_delta.notes}</p>
                )}
              </div>
            )}
          </section>

          <section className="mt-8">
            <div className="overline mb-2">{dt("public_docs.co_signature_heading")}</div>
            {already ? (
              <div className="border border-[var(--jc-border)] rounded-md p-5 bg-[var(--jc-bg)]" data-testid="public-co-signed-block">
                <div className="font-display text-3xl italic text-[var(--jc-navy)]">
                  {customerSigner.signature_name}
                </div>
                <div className="text-xs text-[var(--jc-text-soft)] mt-2">
                  {dt("public_docs.contract_signed_on", formatDate(customerSigner.signed_at, lang))}
                </div>
              </div>
            ) : (
              <div className="border border-[var(--jc-border)] rounded-md p-5">
                <p className="text-sm text-[var(--jc-text-soft)] mb-3">
                  {dt("public_docs.co_consent_language")}
                </p>
                <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">
                  {dt("public_docs.co_sign_input_label")}
                </label>
                <input
                  value={signing}
                  onChange={(e) => setSigning(e.target.value)}
                  data-testid="public-co-sign-input"
                  placeholder={dt("public_docs.co_sign_input_placeholder")}
                  className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
                />
                {error && <div className="text-sm text-[var(--jc-red)] mt-2" data-testid="public-co-error">{error}</div>}
                <button
                  onClick={sign}
                  disabled={submitting}
                  data-testid="public-co-sign-btn"
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold jc-btn-primary disabled:opacity-60"
                >
                  {submitting ? dt("public_docs.contract_sign_submitting") : dt("public_docs.co_sign_btn")}
                </button>
              </div>
            )}
          </section>

          <footer className="mt-8 pt-6 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)] leading-relaxed">
            <div className="mt-2">
              {data.expires_at && dt("public_docs.link_expires_prefix", formatDate(data.expires_at, lang))}
            </div>
          </footer>
        </article>
      </div>
    </PublicShell>
  );
};





const Block = ({ title, body }) => body ? (
  <div>
    <div className="overline mb-1">{title}</div>
    <p className="leading-relaxed whitespace-pre-wrap">{body}</p>
  </div>
) : null;


// Phase 1 shared customer-document standard · Public Invoice
// (`/i/:token`). VIEW / RECORD / DELIVERY only. JobClose AI does not
// collect payment on behalf of the contractor; no Pay Now button, no
// disabled payment button, no placeholder payment link. If the invoice
// snapshot contains contractor-authored `payment_instructions`, they
// are rendered as a read-only text block.
export const PublicInvoice = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/invoices/${token}`).then((r) => setData(r.data)).catch(setErr);
  }, [token]);

  if (err) return <ErrorBox error={err} />;
  if (!data) return <Loading />;

  const lang = data.language || "en";
  const dt = makeTranslator(lang);
  const typeKey = `public_docs.invoice_type_labels.${data.invoice_type}`;
  const typeVal = dt(typeKey);
  const typeLabel = typeVal === typeKey ? (data.invoice_type || "").replace("_", " ") : typeVal;
  const statusKey = `public_docs.invoice_status_labels.${data.payment_status}`;
  const statusVal = dt(statusKey);
  const statusLabel = statusVal === statusKey ? data.payment_status : statusVal;
  const isPaid = data.payment_status === "paid";
  const lineItems = data.line_items || [];
  const subtotal = lineItems.reduce((s, l) => s + (Number(l.total) || 0), 0);
  const total = Number(data.amount || 0);
  const balanceDue = isPaid ? 0 : total;

  return (
    <PublicShell kind="invoice" lang={lang} company={data.company} token={token}>
      <div className="max-w-4xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => window.print()}
            data-testid="public-invoice-print-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white hover:bg-[var(--jc-bg)] print:hidden"
          >
            <Printer size={14} /> {dt("public_docs.print_save_pdf")}
          </button>
        </div>

        <article
          data-testid="public-invoice-article"
          className="bg-white border border-[var(--jc-border)] rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 lg:p-10 print:shadow-none print:border-0"
        >
          <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between border-b border-[var(--jc-border)] pb-6 gap-4">
            <div>
              <div className="overline">{dt("public_docs.invoice_kind_label")}</div>
              <h1 className="font-display text-3xl mt-1" data-testid="public-invoice-title">
                #{(data.id || "").slice(0, 8).toUpperCase()}
              </h1>
              {data.customer?.name ? (
                <div className="text-sm text-[var(--jc-text-soft)] mt-1" data-testid="public-invoice-customer">
                  <div className="overline mb-0.5">{dt("public_docs.invoice_bill_to")}</div>
                  <div className="font-semibold text-[var(--jc-text)]">{data.customer.name}</div>
                  {data.customer.address ? <div>{data.customer.address}</div> : null}
                  {data.customer.email ? <div>{data.customer.email}</div> : null}
                </div>
              ) : null}
            </div>
            <div className="text-sm text-[var(--jc-text-soft)] sm:text-right space-y-1">
              <div>{dt("public_docs.contract_issued_prefix")}: {formatDate(data.created_at, lang)}</div>
              {data.due_date && (
                <div>{dt("public_docs.invoice_due_label")}: {formatDate(data.due_date, lang)}</div>
              )}
              <div className="uppercase tracking-wider text-[11px]">
                {dt("public_docs.invoice_type_label")}: <span className="font-semibold text-[var(--jc-text)]">{typeLabel}</span>
              </div>
              <div
                data-testid="public-invoice-status"
                className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${
                  isPaid
                    ? "bg-[var(--jc-teal)] text-white"
                    : "bg-[var(--jc-bg)] border border-[var(--jc-border-strong)] text-[var(--jc-text)]"
                }`}
              >
                {(statusLabel || "").toUpperCase()}
              </div>
            </div>
          </header>

          {lineItems.length > 0 && (
            <section className="mt-6 overflow-x-auto">
              <div className="overline mb-2">{dt("public_docs.invoice_line_items")}</div>
              <table className="w-full text-sm border border-[var(--jc-border)]">
                <thead className="bg-[var(--jc-bg)]">
                  <tr>
                    <th className="text-left px-3 py-2">{dt("public_docs.proposal_item")}</th>
                    <th className="text-right px-3 py-2">{dt("public_docs.proposal_qty")}</th>
                    <th className="text-right px-3 py-2">{dt("public_docs.proposal_total")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--jc-border)]">
                  {lineItems.map((l, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{l.name || "—"}</div>
                        {l.description ? (
                          <div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td>
                      <td className="px-3 py-2 text-right font-mono-jc">{money(l.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-[var(--jc-bg)] text-sm">
                  <tr>
                    <td colSpan={2} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">
                      {dt("public_docs.proposal_subtotal")}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono-jc">{money(subtotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </section>
          )}

          <section
            className="mt-8 border-t border-[var(--jc-border)] pt-6 flex items-end justify-between gap-4 flex-wrap"
            data-testid="public-invoice-totals"
          >
            <div>
              <div className="overline">{dt("public_docs.invoice_amount_due")}</div>
              <div className="font-display text-4xl mt-1" data-testid="public-invoice-total">
                {money(total)}
              </div>
            </div>
            <div className="text-right">
              <div className="overline">{dt("public_docs.invoice_balance_due")}</div>
              <div
                className={`font-mono-jc text-2xl font-bold ${isPaid ? "text-[var(--jc-teal)]" : "text-[var(--jc-text)]"}`}
                data-testid="public-invoice-balance"
              >
                {money(balanceDue)}
              </div>
            </div>
          </section>

          {data.payment_instructions && (
            <section
              className="mt-6 text-sm border border-[var(--jc-border)] rounded-md p-4 bg-[var(--jc-bg)]"
              data-testid="public-invoice-payment-instructions"
            >
              <div className="overline mb-1">{dt("public_docs.invoice_payment_instructions")}</div>
              <p className="leading-relaxed whitespace-pre-wrap">{data.payment_instructions}</p>
            </section>
          )}

          {data.notes && (
            <section className="mt-6 text-sm" data-testid="public-invoice-notes">
              <div className="overline mb-1">{dt("public_docs.invoice_notes_heading")}</div>
              <p className="leading-relaxed whitespace-pre-wrap">{data.notes}</p>
            </section>
          )}

          <footer className="mt-8 pt-6 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)] leading-relaxed">
            {data.invoice_terms && <p className="whitespace-pre-wrap">{data.invoice_terms}</p>}
            {data.expires_at && (
              <div className="mt-2">{dt("public_docs.link_expires_prefix", formatDate(data.expires_at, lang))}</div>
            )}
          </footer>
        </article>
      </div>
    </PublicShell>
  );
};
